export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function next(): number {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface DecorPlacement {
  x: number;
  y: number;
  variantIndex: number;
  scale: number;
}

export interface ExcludeRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function pointInRect(px: number, py: number, r: ExcludeRect, buffer = 0): boolean {
  return (
    px >= r.x - buffer &&
    px <= r.x + r.w + buffer &&
    py >= r.y - buffer &&
    py <= r.y + r.h + buffer
  );
}

export interface VariantWeight {
  weight: number;
  scaleMin: number;
  scaleMax: number;
}

export function generateDecorMulti(
  worldWidth: number,
  worldHeight: number,
  excludes: ExcludeRect[],
  seed: number,
  variants: VariantWeight[],
  count: number
): DecorPlacement[] {
  const rand = mulberry32(seed);
  const totalWeight = variants.reduce((sum, v) => sum + v.weight, 0);
  if (totalWeight === 0) return [];

  const out: DecorPlacement[] = [];
  const margin = 36;
  let attempts = 0;

  while (out.length < count && attempts < count * 10) {
    attempts++;
    const x = Math.floor(rand() * (worldWidth - margin * 2)) + margin;
    const y = Math.floor(rand() * (worldHeight - margin * 2)) + margin;

    if (excludes.some((ex) => pointInRect(x, y, ex, 24))) continue;

    let r = rand() * totalWeight;
    let variantIndex = 0;
    for (let i = 0; i < variants.length; i++) {
      r -= variants[i].weight;
      if (r <= 0) {
        variantIndex = i;
        break;
      }
    }
    const v = variants[variantIndex];
    const scale = v.scaleMin + rand() * (v.scaleMax - v.scaleMin);
    out.push({ x, y, variantIndex, scale });
  }

  return out;
}

export interface PatchPlacement {
  x: number;
  y: number;
  size: number;
  patchIndex: number;
}

export interface PatchSpec {
  count: number;
  minSize: number;
  maxSize: number;
}

export function generateGroundPatches(
  worldWidth: number,
  worldHeight: number,
  seed: number,
  patchSpecs: PatchSpec[]
): PatchPlacement[] {
  const rand = mulberry32(seed ^ 0xa5a5a5);
  const out: PatchPlacement[] = [];
  for (let i = 0; i < patchSpecs.length; i++) {
    const spec = patchSpecs[i];
    for (let n = 0; n < spec.count; n++) {
      const size = spec.minSize + rand() * (spec.maxSize - spec.minSize);
      out.push({
        x: rand() * worldWidth,
        y: rand() * worldHeight,
        size,
        patchIndex: i
      });
    }
  }
  return out;
}

export function pickRandomSpawn(
  worldWidth: number,
  worldHeight: number,
  exclude: ExcludeRect,
  rand: () => number,
  awayFrom?: { x: number; y: number; minDist: number }
): { x: number; y: number } {
  const margin = 48;
  for (let i = 0; i < 60; i++) {
    const x = margin + rand() * (worldWidth - margin * 2);
    const y = margin + rand() * (worldHeight - margin * 2);

    if (pointInRect(x, y, exclude, margin)) continue;

    if (awayFrom) {
      const dx = x - awayFrom.x;
      const dy = y - awayFrom.y;
      if (dx * dx + dy * dy < awayFrom.minDist * awayFrom.minDist) continue;
    }

    return { x, y };
  }
  return { x: worldWidth / 2, y: worldHeight / 2 };
}
